import { useTranslation, Translation } from 'react-i18next';
import React, { ReactNode, memo, useMemo, useCallback, useEffect, useRef } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { NavLink, useHistory } from 'react-router-dom';
import cx from 'clsx';

import { IconProposalDropdown, IconProposalEmpty } from '@/assets/proposals';
import { DesktopHeader2, Button, DropdownFilter, NotConnected, SearchInput } from '@/components';
import { useQueryParams, useSwitch, useDeviceTypeIsMobile } from '@/hooks';
import { useCurrentUserId, login, openInDebank } from '@/utils';

import { ProposalItem } from '../types';
import { useProposalList, invalidProposalList } from '../hooks';
import { CreatePropsalModal, ProposalStatus, ProposalComment, ProposalVote } from '../components';
import { CustomTitle } from '@/components/CustomTitle';

import style from './index.module.scss';

const SORT_BY: Array<{ label: ReactNode; value: string }> = [
  {
    label: <Translation>{t => t('ListContainer.yv8wg5')}</Translation>,
    value: '-create_at',
  },
  {
    label: <Translation>{t => t('ListContainer.1dbn3d')}</Translation>,
    value: '-voter_count',
  },
  {
    label: <Translation>{t => t('ListContainer.12gh4bt')}</Translation>,
    value: '-usd_value',
  },
];

const Container = memo(({ isMine }: { isMine: boolean }) => {
  const { t } = useTranslation();

  const history = useHistory();
  const [orderBy, setOrderBy] = useQueryParams('order_dy', SORT_BY[0].value);
  const { on, turnOn, turnOff } = useSwitch();
  const isMobile = useDeviceTypeIsMobile();
  const userId = useCurrentUserId();
  const [searchText, setSearchText] = useQueryParams<string>('q', '');
  const searchRef = useRef<HTMLInputElement>(null);

  const isLogged = useMemo(() => !!userId, [userId]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch } =
    useProposalList(searchText, orderBy, isMine, isMobile);

  const flattenProposals = useMemo(() => data?.flat(), [data]);

  const fetchMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleMyProposalClick = useCallback(() => {
    if (isMobile) {
      openInDebank({ link: 'vote/mine' });

      return;
    }

    if (!isLogged) {
      login();
      return;
    }

    setSearchText('');
    if (searchRef?.current) {
      searchRef.current.value = '';
    }
    setTimeout(() => {
      history.push('/proposal/mine');
    });
  }, [history, isLogged, isMobile, setSearchText]);

  const handleSortClick = useCallback(
    ({ currentTarget }: React.MouseEvent<HTMLSpanElement>) => {
      const { value } = currentTarget.dataset;

      if (value) {
        setOrderBy(value);
        if (searchRef?.current) {
          setSearchText(searchRef.current.value);
        }
      }
    },
    [setOrderBy, setSearchText],
  );

  const isEmpty = useMemo(
    () => !flattenProposals?.length && !isLoading,
    [isLoading, flattenProposals?.length],
  );

  const handleCreate = useCallback(() => {
    if (!isLogged) {
      login();
      return;
    }

    turnOn();
  }, [isLogged, turnOn]);

  useEffect(() => {
    if (isMobile && isMine) {
      openInDebank({ link: 'vote/mine', showDownloadGuide: false });
    }
  }, [isMobile, isMine]);

  const handleSearch = useCallback(
    ({ currentTarget }: React.KeyboardEvent<HTMLInputElement>) => {
      const { value } = currentTarget;

      if (value === searchText) {
        invalidProposalList(isMine);
      }

      setSearchText(value);
    },
    [isMine, searchText, setSearchText],
  );

  return (
    <>
      <CustomTitle titlePrefix="Vote" />
      {isMobile ? null : (
        <DesktopHeader2
          title={isMine ? t('ListContainer.47psa5') : t('ListContainer.1la16')}
          enableBack={isMine}
        />
      )}
      <div className={cx(style.main, isMobile && style.mobile)}>
        {isMobile ? (
          <MobileHeader
            onOrderChange={setOrderBy}
            orderBy={orderBy}
            isMine={isMine}
            onMineClick={handleMyProposalClick}
          />
        ) : isMine ? null : (
          <div className={style.header}>
            <div className={style.sorts}>
              <span className={style.sortTitle}>{t('ListContainer.sizb6p')}</span>
              {SORT_BY.map(sort => (
                <span
                  key={sort.value}
                  className={cx(style.sortLabel, sort.value === orderBy && style.isSelected)}
                  onClick={handleSortClick}
                  data-value={sort.value}>
                  {sort.label}
                </span>
              ))}
            </div>
            <div>
              <Button type="text" className={style.myProposalBtn} onClick={handleMyProposalClick}>
                {t('ListContainer.47psa5')}
              </Button>
              <Button type="primary" className={style.createProposalBtn} onClick={handleCreate}>
                {t('ListContainer.ehmt4m')}
              </Button>
            </div>
          </div>
        )}
        {isMine ? null : (
          <SearchInput
            placeholder="Search"
            onPressEnter={handleSearch}
            className={style.searchInput}
            ref={searchRef}
            defaultValue={searchText}
            activeClassName
            key={`${isMine}`}
          />
        )}
        <div className={cx(style.lists, isEmpty && style.isEmpty)}>
          {isEmpty ? (
            <div>
              {isMine && !isLogged && !isMobile ? (
                <NotConnected withIcon shadow={false} />
              ) : (
                <>
                  <img src={IconProposalEmpty} alt="empty" />
                  <div className={style.emptyTitle}>{t('ListContainer.1vwh8ub')}</div>
                </>
              )}
            </div>
          ) : (
            <Virtuoso
              useWindowScroll
              data={flattenProposals}
              totalCount={flattenProposals?.length}
              overscan={10}
              endReached={fetchMore}
              key={searchText + isMobile + orderBy + isMine + userId}
              itemContent={(index, item) => (
                <ProposalListItem
                  key={item.id}
                  data={item}
                  isLogged={isLogged}
                  border={!!flattenProposals?.length && index !== flattenProposals?.length - 1}
                  source="list_page"
                />
              )}
            />
          )}
        </div>
      </div>
      {on ? <CreatePropsalModal turnOff={turnOff} onUpdate={refetch} /> : null}
    </>
  );
});

const MobileHeader = ({
  onOrderChange,
  onMineClick,
  orderBy,
  isMine,
}: {
  onMineClick: () => void;
  onOrderChange: (v: string) => void;
  orderBy: string;
  isMine: boolean;
}) => {
  const { t } = useTranslation();

  const currentFilterIdx = useMemo(() => SORT_BY.findIndex(x => x.value === orderBy), [orderBy]);

  const onFilterClick = useCallback(
    idx => {
      onOrderChange(SORT_BY[idx].value);
    },
    [onOrderChange],
  );

  return (
    <div className={style.mobileHeader}>
      {isMine ? null : (
        <>
          <DropdownFilter
            filters={SORT_BY}
            onFilterChange={onFilterClick}
            currentFilterIdx={currentFilterIdx}
            showCurrent
            labelClassName={style.mobileProposalSort}>
            <img src={IconProposalDropdown} alt="" className={style.mobileProposalIcon} />
          </DropdownFilter>
          <Button type="text" className={style.myProposalMobileBtn} onClick={onMineClick}>
            {t('ListContainer.47psa5')}
          </Button>
        </>
      )}
    </div>
  );
};

export const ProposalListItem = memo(
  ({
    data,
    border,
    isLogged,
    customClass,
    onUpdate,
    urlTarget = '_self',
    source,
  }: {
    data: ProposalItem;
    border?: boolean;
    customClass?: string;
    isLogged: boolean;
    onUpdate?: () => void;
    urlTarget?: '_blank' | '_self';
    source: string;
  }) => {
    return (
      <NavLink to={`/proposal/${data.id}`} target={urlTarget}>
        <div className={cx(style.proposalRecord, !border && style.noBorder, customClass)}>
          <ProposalVote
            data={data}
            className={style.proposalVote}
            isLogged={isLogged}
            onVote={onUpdate}
            source={source}
            disabled
          />
          <div className={style.proposalRecordBody}>
            <div className={style.proposalRecordTitle}>{data.description}</div>
            <ProposalStatus data={data} className={style.proposalStatus} />
            <ProposalComment comment={data.reply_msg} className={style.proposalComment} />
          </div>
        </div>
      </NavLink>
    );
  },
);

export default Container;
